/**
 * Repository SDD Page
 *
 * List and manage SDDs for a repository
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Loader2 } from 'lucide-react';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@forgenetwork/ui';
import { SDDList } from '@/components/sdd';
import { useGenerateSDD } from '@/hooks/useSDD';
import { useToast } from '@/shared/hooks/useToast';

export const Route = createFileRoute('/_authenticated/repositories/$repoId/sdd')({
  component: RepositorySDDPage,
});

function RepositorySDDPage(): React.JSX.Element {
  const { repoId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const generateSDD = useGenerateSDD();

  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [newSddTitle, setNewSddTitle] = React.useState('');
  const [newSddContent, setNewSddContent] = React.useState('');

  const handleCreateSDD = (): void => {
    if (!newSddTitle.trim()) {
      toast({
        title: 'Title Required',
        description: 'Please enter a title for the SDD.',
        variant: 'destructive',
      });
      return;
    }

    generateSDD.mutate(
      {
        repoId,
        data: {
          title: newSddTitle,
          content: newSddContent || '## Overview\n\nDescribe your system here...',
        },
      },
      {
        onSuccess: (data) => {
          toast({
            title: 'SDD Created',
            description: 'Your SDD has been created successfully.',
          });
          setCreateDialogOpen(false);
          setNewSddTitle('');
          setNewSddContent('');
          navigate({
            to: '/repositories/$repoId/sdd/$sddId',
            params: { repoId, sddId: data.id },
            search: { tab: 'edit' },
          });
        },
        onError: (error: Error) => {
          toast({
            title: 'Creation Failed',
            description: error.message || 'Failed to create SDD',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: '/repositories/$repoId', params: { repoId } })}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                System Design Documents
              </h1>
              <p className="text-muted-foreground mt-1">
                Create and manage SDDs for this repository
              </p>
            </div>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            disabled={generateSDD.isPending}
            className="gap-2"
          >
            {generateSDD.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                New SDD
              </>
            )}
          </Button>
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>About SDDs</CardTitle>
            <CardDescription>
              System Design Documents (SDDs) capture your architecture decisions,
              technical specifications, and system design. They can be created from
              code analysis or written manually to document your system.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* SDD List */}
        <SDDList
          repositoryId={repoId}
          onCreateNew={() => setCreateDialogOpen(true)}
        />
      </div>

      {/* Create SDD Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>Create New SDD</DialogTitle>
            <DialogDescription>
              Create a new System Design Document for this repository.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={newSddTitle}
                onChange={(e) => setNewSddTitle(e.target.value)}
                placeholder="System Architecture Overview"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Initial Content (optional)</Label>
              <Textarea
                id="content"
                value={newSddContent}
                onChange={(e) => setNewSddContent(e.target.value)}
                placeholder="## Overview\n\nDescribe your system here..."
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                You can add more sections after creating the SDD.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSDD} disabled={generateSDD.isPending}>
              {generateSDD.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create SDD'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
